import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Phone, Mail, CalendarDays, Filter, Trophy, CircleDollarSign, BarChart3, Target, Rocket, Zap, ChevronRight, Sparkles } from 'lucide-react';
import { reducedMotion, useCountUp, Sparkline } from '../../shared/Motion';
import { Ring } from '../CrmHomeWidgets';

// Presentation only — every number comes from GET /crm/performance (actual / target / pct / series).
export const METRIC_STYLE = {
  new_leads: { icon: Users, color: '#7c3aed', tile: 'bg-violet-50 text-violet-600', tint: 'to-violet-50/60' },
  calls: { icon: Phone, color: '#2563eb', tile: 'bg-blue-50 text-blue-600', tint: 'to-blue-50/60' },
  emails: { icon: Mail, color: '#f97316', tile: 'bg-orange-50 text-orange-500', tint: 'to-orange-50/60' },
  meetings: { icon: CalendarDays, color: '#ec4899', tile: 'bg-pink-50 text-pink-500', tint: 'to-pink-50/60' },
  qualified_opportunities: { icon: Filter, color: '#10b981', tile: 'bg-emerald-50 text-emerald-600', tint: 'to-emerald-50/60' },
  won_opportunities: { icon: Trophy, color: '#f59e0b', tile: 'bg-amber-50 text-amber-500', tint: 'to-amber-50/60' },
  revenue_won: { icon: CircleDollarSign, color: '#0ea5e9', tile: 'bg-sky-50 text-sky-600', tint: 'to-sky-50/60' },
};
const styleFor = (key) => METRIC_STYLE[key] || METRIC_STYLE.new_leads;
export const fmtVal = (v, kind) => kind === 'currency' ? `$${Number(v || 0).toLocaleString()}` : Number(v || 0).toLocaleString();
const fmtTarget = (m) => m.target > 0 ? fmtVal(m.target, m.kind) : '—';

export const PerfHeader = ({ subtitle, children }) => (
  <section className="lv-card an-rise relative overflow-hidden px-5 py-4 flex flex-wrap items-center gap-4 bg-gradient-to-r from-white via-white to-violet-50/70" data-testid="perf-header">
    <span className="h-12 w-12 shrink-0 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center"><BarChart3 className="h-6 w-6" /></span>
    <div className="min-w-[200px] flex-1">
      <h1 className="crm-page-title">My Performance</h1>
      <p className="text-[13px] text-slate-500 mt-0.5">{subtitle}</p>
    </div>
    <div className="relative z-10 flex items-center gap-2">{children}</div>
    <div className="hidden lg:flex items-center gap-4 pl-4 pr-1 shrink-0" data-testid="perf-illustration">
      <img src="/crm-perf-target.png" alt="" className="h-[84px] w-auto select-none pointer-events-none drop-shadow-sm" />
      <p className="hidden xl:block an-accent text-[19px] leading-6 text-violet-600 w-[150px]">Big Goals.<br />Bigger Results! <Sparkles className="inline h-3.5 w-3.5 -mt-1 text-violet-400" /></p>
    </div>
  </section>
);

export const PerfKpiCard = ({ m, delay = 0 }) => {
  const st = styleFor(m.key); const Icon = st.icon; const n = useCountUp(m.actual);
  const flat = !(m.series || []).some(v => v > 0);
  return (
    <div className={`lv-card lv-lift an-rise p-4 flex items-center gap-3 bg-gradient-to-br from-white ${st.tint}`} style={{ animationDelay: `${delay}ms` }} data-testid={`kpi-${m.key}`}>
      <span className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${st.tile}`}><Icon className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold text-slate-600 truncate">{m.label}</p>
        <p className="text-[26px] leading-7 font-extrabold text-slate-900 tabular-nums mt-0.5" data-testid={`kpi-${m.key}-value`}>{n == null ? '…' : fmtVal(n, m.kind)}</p>
        <p className="text-[11.5px] text-slate-400 mt-0.5">Target <span className="text-slate-500 font-medium" data-testid={`kpi-${m.key}-target`}>{fmtTarget(m)}</span></p>
      </div>
      <div className="shrink-0 hidden sm:block" title={flat ? 'No activity recorded this month yet' : `Daily ${m.label.toLowerCase()} this month`} data-testid={`kpi-${m.key}-spark`}>
        <Sparkline data={m.series?.length > 1 ? m.series : [0, 0]} color={flat ? '#cbd5e1' : st.color} w={92} h={38} testid="perf-sparkline" />
      </div>
    </div>
  );
};

const useGrow = (pct) => {
  const [w, setW] = useState(reducedMotion() ? pct : 0);
  useEffect(() => { const id = requestAnimationFrame(() => setW(pct)); return () => cancelAnimationFrame(id); }, [pct]);
  return w;
};

export const BreakdownRow = ({ m }) => {
  const st = styleFor(m.key); const Icon = st.icon;
  const has = m.target > 0; const fill = useGrow(has ? Math.min(m.pct, 100) : 0);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[210px_minmax(0,1fr)_auto_auto] items-center gap-x-4 gap-y-1.5 py-2.5 border-t border-slate-100 first:border-t-0" data-testid={`perf-${m.key}`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${st.tile}`}><Icon className="h-4 w-4" /></span>
        <span className="text-[13px] font-medium text-slate-800 leading-tight">{m.label}</span>
      </div>
      <div className="col-span-2 sm:col-span-1 order-3 sm:order-none h-2 rounded-full overflow-hidden" style={{ background: `${st.color}22` }}>
        <div className="pf-bar h-full rounded-full" style={{ width: `${fill}%`, background: st.color }} data-testid={`perf-bar-${m.key}`} />
      </div>
      <div className="text-[13px] tabular-nums whitespace-nowrap text-right sm:min-w-[92px]">
        <span className="font-semibold text-slate-900" data-testid={`perf-actual-${m.key}`}>{fmtVal(m.actual, m.kind)}</span>
        <span className="text-slate-400"> / </span><span className="text-slate-500" data-testid={`perf-target-${m.key}`}>{fmtTarget(m)}</span>
      </div>
      <div className="justify-self-end">
        {has ? <span className={`inline-flex min-w-[46px] justify-center rounded-md px-1.5 py-0.5 text-[11.5px] font-bold tabular-nums ${m.pct >= 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`} data-testid={`perf-pct-${m.key}`}>{m.pct}%</span>
          : <span className="inline-flex rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold bg-slate-100 text-slate-400 whitespace-nowrap" data-testid={`perf-notarget-${m.key}`}>No target set</span>}
      </div>
    </div>
  );
};

// 2a: mean of per-metric achievement (each capped at 100) over metrics WITH a target; null when none configured.
export const monthlyProgress = (metrics = []) => {
  const withT = metrics.filter(m => m.target > 0);
  if (!withT.length) return null;
  return Math.round((withT.reduce((s, m) => s + Math.min(m.pct ?? 0, 100), 0) / withT.length) * 10) / 10;
};
const mood = (p) => p >= 100 ? 'All targets met — outstanding work!' : p >= 50 ? 'Great momentum — you\'re over halfway there.' : 'You\'re on your way! Keep going and make it happen.';

export const MonthPanel = ({ metrics, monthLabel, loading, setTargetsTo }) => {
  const p = monthlyProgress(metrics);
  return (
    <section className="lv-card an-rise p-4" style={{ animationDelay: '260ms' }} data-testid="perf-month-panel">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><CalendarDays className="h-4 w-4 text-violet-500" /> This Month</h2>
        <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500"><CalendarDays className="h-3.5 w-3.5 text-slate-400" />{monthLabel}</span>
      </div>
      {loading ? <div className="mt-4 flex items-center gap-4 animate-pulse"><div className="h-24 w-24 rounded-full bg-slate-100" /><div className="flex-1 space-y-2"><div className="h-3.5 w-40 rounded bg-slate-100" /><div className="h-3 w-52 rounded bg-slate-100" /></div></div>
        : p == null ? (
          <div className="mt-4 flex items-center gap-4" data-testid="perf-month-empty">
            <span className="h-24 w-24 shrink-0 rounded-full border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300"><Target className="h-8 w-8" /></span>
            <div className="min-w-0">
              <p className="text-[14.5px] font-bold text-slate-800">Targets not set yet</p>
              <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">Monthly progress will appear once targets are configured.</p>
              {setTargetsTo && <Link to={setTargetsTo} className="inline-flex items-center gap-1 mt-2 text-[12.5px] font-semibold text-violet-700 hover:text-violet-900" data-testid="perf-month-set-targets">Set targets <ChevronRight className="h-3.5 w-3.5" /></Link>}
            </div>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-4" data-testid="perf-month-progress">
            <div className="relative shrink-0"><Ring pct={p} color="#6d4ee0" size={96} stroke={9} /><span className="absolute inset-0 flex items-center justify-center text-[20px] font-extrabold text-slate-900 tabular-nums" data-testid="perf-month-pct">{Math.round(p)}%</span></div>
            <div className="min-w-0"><p className="text-[14.5px] font-bold text-slate-800">Monthly Target Progress</p><p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">{mood(p)}</p></div>
          </div>
        )}
      <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/70 px-3.5 py-3 flex items-center gap-3" data-testid="perf-motivation">
        <Rocket className="h-5 w-5 text-violet-500 shrink-0" />
        <p className="text-[12.5px] leading-snug text-violet-800">Every call, email, and meeting brings you closer to your goals!</p>
      </div>
    </section>
  );
};

export const QuickActions = ({ items }) => (
  <section className="lv-card an-rise p-4" style={{ animationDelay: '320ms' }} data-testid="perf-quick-actions">
    <h2 className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><Zap className="h-4 w-4 text-violet-500" /> Quick Actions</h2>
    <div className="mt-3 space-y-2">
      {items.map(({ label, icon: Icon, to, onClick, testid }) => {
        const inner = <><span className="h-7 w-7 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Icon className="h-3.5 w-3.5" /></span><span className="flex-1 text-[13px] font-medium text-slate-800">{label}</span><ChevronRight className="pf-qa-arrow h-4 w-4 text-slate-400" /></>;
        const cls = 'pf-qa w-full flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 text-left hover:border-violet-200 hover:bg-violet-50/40';
        return to ? <Link key={testid} to={to} className={cls} data-testid={testid}>{inner}</Link> : <button key={testid} type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
      })}
    </div>
  </section>
);

export const KpiSkeleton = () => (
  <div className="lv-card p-4 flex items-center gap-3 animate-pulse" data-testid="skeleton-row"><div className="h-11 w-11 rounded-xl bg-slate-100" /><div className="flex-1 space-y-2"><div className="h-3 w-24 rounded bg-slate-100" /><div className="h-6 w-14 rounded bg-slate-100" /><div className="h-2.5 w-16 rounded bg-slate-100" /></div><div className="h-9 w-[92px] rounded bg-slate-100 hidden sm:block" /></div>
);
